import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import { FileText, Loader2, Wrench, Sliders } from 'lucide-react';
import api from '../../../services/api';
import { PreviewContext } from '../PreviewContext';

// Tab configuration
const TABS = [
  { id: 'skills', label: 'Select Skills', icon: Wrench, description: 'Import from CV or add skills' },
  { id: 'design', label: 'Design & Proficiency', icon: Sliders, description: 'Style and skill levels' },
];

const SkillsSelection = ({ onNext, updateLiveSkills, savedProficiencies, setSavedProficiencies, savedCategories, setSavedCategories, savedDescriptions, setSavedDescriptions, parentSelectedSkills, setParentSelectedSkills, showProficiency = true }) => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState(parentSelectedSkills || []);
  const [customSkill, setCustomSkill] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');

  // Sync parent state when local selectedSkills changes
  useEffect(() => {
    if (setParentSelectedSkills && selectedSkills.length > 0) {
      setParentSelectedSkills(selectedSkills);
    }
  }, [selectedSkills, setParentSelectedSkills]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    // Fetch skills already added in the database or AI-generated skills
    api.get('/career/skill/', {
      headers: { Authorization: `Token ${token}` },
      withCredentials: true,
    })
      .then((response) => {
        let dbSkills = [];
        let proficiencyMap = {};
        let categoryMap = {};
        let descriptionMap = {};
        if (Array.isArray(response.data)) {
          dbSkills = response.data.map((skill) => skill.name);
          // Store proficiency, category, and description values from database
          response.data.forEach((skill) => {
            proficiencyMap[skill.name] = skill.proficiency || 50;
            categoryMap[skill.name] = skill.category || 'other';
            descriptionMap[skill.name] = skill.description || '';
          });
        } else if (response.data.skills) {
          dbSkills = response.data.skills;
        }
        setSkills(dbSkills);
        setSelectedSkills(dbSkills);
        setSavedProficiencies(proficiencyMap);
        setSavedCategories(categoryMap);
        if (setSavedDescriptions) setSavedDescriptions(descriptionMap);
        // Update live preview
        if (updateLiveSkills) {
          updateLiveSkills(response.data);
        }
      })
      .catch((error) => console.error('Error fetching skills:', error));
  }, [setSavedProficiencies, setSavedCategories, updateLiveSkills]);

  const toggleSkill = async (skill) => {
    const token = localStorage.getItem('token');
    if (selectedSkills.includes(skill)) {
      // Deselecting - delete from database
      setSelectedSkills(selectedSkills.filter((s) => s !== skill));
      try {
        await api.delete(`/career/skill/delete_by_name/`, {
          data: { name: skill },
          headers: { Authorization: `Token ${token}` },
        });
      } catch (error) {
        console.error('Error deleting skill:', error);
      }
    } else {
      // Selecting - add to database
      setSelectedSkills([...selectedSkills, skill]);
      try {
        await api.post('/career/skill/',
          { name: skill, proficiency: 50, category: 'other' },
          { headers: { Authorization: `Token ${token}` } }
        );
      } catch (error) {
        console.error('Error adding skill:', error);
      }
    }
  };

  const handleAddCustomSkill = async () => {
    const trimmedSkill = customSkill.trim();
    if (trimmedSkill !== '') {
      if (!skills.includes(trimmedSkill)) {
        setSkills([...skills, trimmedSkill]);
      }
      if (!selectedSkills.includes(trimmedSkill)) {
        setSelectedSkills([...selectedSkills, trimmedSkill]);
        // Save to database immediately
        try {
          const token = localStorage.getItem('token');
          await api.post('/career/skill/',
            { name: trimmedSkill, proficiency: 50, category: 'other' },
            { headers: { Authorization: `Token ${token}` } }
          );
        } catch (error) {
          console.error('Error adding custom skill:', error);
        }
      }
      setCustomSkill('');
    }
  };

  const handleNext = () => {
    if (showProficiency) {
      // Go to design tab to set proficiency
      onNext(selectedSkills);
    } else {
      // Skip proficiency, go directly to next section
      navigate('/dashboard/services');
    }
  };

  const fetchSkills = () => {
    const token = localStorage.getItem('token');
    api.get('/career/skill/', {
      headers: { Authorization: `Token ${token}` },
      withCredentials: true,
    })
      .then((response) => {
        let dbSkills = [];
        let categoryMap = {};
        if (Array.isArray(response.data)) {
          dbSkills = response.data.map((skill) => skill.name);
          // Also update categories
          response.data.forEach((skill) => {
            categoryMap[skill.name] = skill.category || 'other';
          });
        } else if (response.data.skills) {
          dbSkills = response.data.skills;
        }
        setSkills(dbSkills);
        setSelectedSkills(dbSkills);
        if (setSavedCategories && Object.keys(categoryMap).length > 0) {
          setSavedCategories(categoryMap);
        }
        // Update live preview
        if (updateLiveSkills) {
          updateLiveSkills(Array.isArray(response.data) ? response.data : []);
        }
      })
      .catch((error) => console.error('Error fetching skills:', error));
  };

  const handleImportFromCV = async () => {
    setIsImporting(true);
    setImportMessage('');
    try {
      const response = await api.post('/ai/import-from-cv/', { section: 'skills' });
      if (response.data.success) {
        const importedCount = response.data.imported.skills;
        setImportMessage(`Imported ${importedCount} skills. Auto-categorizing...`);
        fetchSkills();

        // Automatically categorize skills after import
        if (importedCount > 0) {
          try {
            const token = localStorage.getItem('token');
            const catResponse = await api.post('/ai/categorize-skills/', {}, {
              headers: { Authorization: `Token ${token}` }
            });
            if (catResponse.data.success) {
              setImportMessage(`Imported ${importedCount} skills and categorized ${catResponse.data.updated} using AI!`);
              if (catResponse.data.categories && setSavedCategories) {
                setSavedCategories(catResponse.data.categories);
              }
              fetchSkills();
            }
          } catch (catError) {
            // Categorization failed but import succeeded
            setImportMessage(`Imported ${importedCount} skills (auto-categorization unavailable)`);
          }
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to import from CV';
      setImportMessage(errorMsg);
    } finally {
      setIsImporting(false);
      setTimeout(() => setImportMessage(''), 5000);
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6 bg-white shadow-md rounded-xl">
      <h2 className="text-xl font-semibold mb-4 text-center">Select Your Skills</h2>

      {/* Import from CV Button */}
      <div className="mb-4 space-y-2">
        <button
          onClick={handleImportFromCV}
          disabled={isImporting}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isImporting ? (
            <>
              <Loader2 className="animate-spin" size={18} />
              Importing Skills from CV...
            </>
          ) : (
            <>
              <FileText size={18} />
              Import Skills from CV
            </>
          )}
        </button>

        {importMessage && (
          <div className={`mt-2 p-2 rounded text-sm text-center ${
            importMessage.includes('Imported') || importMessage.includes('Categorized')
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {importMessage}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {skills.length > 0 ? (
          skills.map((skill, index) => (
            <button
              key={index}
              onClick={() => toggleSkill(skill)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border shadow-md flex items-center gap-2 ${
                selectedSkills.includes(skill)
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-slate-700 text-slate-200 border-slate-500 hover:bg-slate-600 hover:border-slate-400'
              }`}
            >
              {selectedSkills.includes(skill) ? (
                <FaCheck className="text-white" />
              ) : (
                <span className="text-slate-400">+</span>
              )}{' '}
              {skill}
            </button>
          ))
        ) : (
          <p>Loading skills...</p>
        )}
      </div>
      <div className="flex mt-4">
        <input
          type="text"
          value={customSkill}
          onChange={(e) => setCustomSkill(e.target.value)}
          placeholder="Enter custom skill"
          className="flex-grow p-2 border border-gray-300 rounded-l-lg focus:outline-none"
        />
        <button
          type="button"
          onClick={handleAddCustomSkill}
          className="bg-green-600 text-white px-4 rounded-r-lg hover:bg-green-700 transition-all"
        >
          Add Skill
        </button>
      </div>
      <button
        onClick={handleNext}
        disabled={selectedSkills.length === 0}
        className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-all disabled:bg-gray-400"
      >
        {showProficiency ? 'Next' : 'Continue to Services →'}
      </button>
    </div>
  );
};

const CATEGORY_OPTIONS = [
  { value: 'programming', label: 'Programming' },
  { value: 'tools', label: 'Tools & Software' },
  { value: 'design', label: 'Design' },
  { value: 'database', label: 'Database' },
  { value: 'cloud', label: 'Cloud & DevOps' },
  { value: 'soft_skills', label: 'Soft Skills' },
  { value: 'other', label: 'Other' },
];

const ProficiencyForm = ({ selectedSkills, onSubmit, savedProficiencies, savedCategories, savedDescriptions, updateLiveSkills, showProficiency = true }) => {
  const [skillData, setSkillData] = useState({});
  const [saveStatus, setSaveStatus] = useState({});

  useEffect(() => {
    const initialData = {};
    selectedSkills.forEach((skill) => {
      // Use saved proficiency if available (convert 1-5 scale to 0-100)
      const savedProf = savedProficiencies[skill];
      const savedCat = savedCategories?.[skill] || 'other';
      const savedDesc = savedDescriptions?.[skill] || '';
      initialData[skill] = {
        proficiency: savedProf ? (savedProf <= 5 ? savedProf * 20 : savedProf) : 50,
        category: savedCat,
        description: savedDesc,
      };
    });
    setSkillData(initialData);
  }, [selectedSkills, savedProficiencies, savedCategories, savedDescriptions]);

  // Auto-save skill changes
  const autoSaveSkill = async (skillName, proficiency, category, description) => {
    try {
      const token = localStorage.getItem('token');
      await api.post('/career/skill/update_skill/',
        { name: skillName, proficiency, category, description: description || '' },
        { headers: { Authorization: `Token ${token}` } }
      );
      setSaveStatus(prev => ({ ...prev, [skillName]: 'saved' }));
      setTimeout(() => setSaveStatus(prev => ({ ...prev, [skillName]: '' })), 1500);

      // Update live preview - fetch fresh data
      if (updateLiveSkills) {
        const response = await api.get('/career/skill/', {
          headers: { Authorization: `Token ${token}` }
        });
        updateLiveSkills(Array.isArray(response.data) ? response.data : response.data.results || []);
      }
    } catch (error) {
      console.error('Error saving skill:', error);
      setSaveStatus(prev => ({ ...prev, [skillName]: 'error' }));
    }
  };

  const handleChange = (skill, field, value) => {
    const newValue = field === 'proficiency' ? Number(value) : value;
    const newSkillData = {
      ...skillData,
      [skill]: {
        ...skillData[skill],
        [field]: newValue,
      },
    };
    setSkillData(newSkillData);

    // Auto-save after change (debounce description saves)
    const skillInfo = newSkillData[skill];
    if (field === 'description') {
      // Debounce description saves
      clearTimeout(window.skillDescriptionTimeout);
      window.skillDescriptionTimeout = setTimeout(() => {
        autoSaveSkill(skill, skillInfo.proficiency, skillInfo.category, skillInfo.description);
      }, 500);
    } else {
      autoSaveSkill(skill, skillInfo.proficiency, skillInfo.category, skillInfo.description);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(skillData);
  };

  // Get color based on proficiency
  const getColor = (proficiency) => {
    if (proficiency >= 80) return '#22c55e';
    if (proficiency >= 60) return '#3b82f6';
    if (proficiency >= 40) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-6 bg-white shadow-md rounded-xl">
      <h2 className="text-xl font-semibold mb-6 text-center">
        {showProficiency ? 'Set Your Proficiency Level' : 'Customize Your Skills'}
      </h2>
      <div className="space-y-6">
        {selectedSkills.map((skill, index) => (
          <div key={index} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <label className="font-semibold text-gray-800">{skill}</label>
              {showProficiency && (
                <span
                  className="text-xl font-bold px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: `${getColor(skillData[skill]?.proficiency || 50)}20`,
                    color: getColor(skillData[skill]?.proficiency || 50),
                  }}
                >
                  {skillData[skill]?.proficiency || 50}%
                </span>
              )}
            </div>

            {/* Proficiency Slider - only show when showProficiency is true */}
            {showProficiency && (
              <div className="mb-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={skillData[skill]?.proficiency || 50}
                  onChange={(e) => handleChange(skill, 'proficiency', e.target.value)}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, ${getColor(skillData[skill]?.proficiency || 50)} ${skillData[skill]?.proficiency || 50}%, #e5e7eb ${skillData[skill]?.proficiency || 50}%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Beginner</span>
                  <span>Intermediate</span>
                  <span>Expert</span>
                </div>
              </div>
            )}

            {/* Category Selection */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Category:</label>
              <select
                value={skillData[skill]?.category || 'other'}
                onChange={(e) => handleChange(skill, 'category', e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes/Description Section */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <span className="w-5 h-5 bg-amber-100 rounded flex items-center justify-center">
                    <span className="text-amber-600 text-xs">📝</span>
                  </span>
                  Skill Story / Notes
                </label>
                {saveStatus[skill] === 'saved' && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    ✓ Saved
                  </span>
                )}
              </div>
              <textarea
                value={skillData[skill]?.description || ''}
                onChange={(e) => handleChange(skill, 'description', e.target.value)}
                placeholder="Tell your story with this skill... How did you learn it? What projects have you used it in? Any certifications?"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none bg-amber-50/30"
                rows={3}
              />
              <p className="text-xs text-gray-400 mt-1">
                This will show as a clickable story on your public profile
              </p>
            </div>
          </div>
        ))}
      </div>
      <button
        type="submit"
        className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg"
      >
        Save & Continue
      </button>
    </form>
  );
};

const SkillForm = () => {
  const navigate = useNavigate();
  const { updateLiveSkills, liveSkillsDesign, updateLiveSkillsDesign, registerSectionTabs, clearSectionTabs } = useContext(PreviewContext);

  // Get showProficiency from design config (default true)
  const showProficiency = liveSkillsDesign?.showProficiency !== false;

  // Handle proficiency toggle
  const handleToggleProficiency = async () => {
    const newValue = !showProficiency;
    const updatedDesign = { ...liveSkillsDesign, showProficiency: newValue };
    if (updateLiveSkillsDesign) {
      updateLiveSkillsDesign(updatedDesign);
    }
    try {
      const token = localStorage.getItem('token');
      await api.post('/career/skills-design/update/', { design_config: updatedDesign }, { headers: { Authorization: `Token ${token}` } });
    } catch (error) {
      console.error('Error saving proficiency setting:', error);
    }
  };
  const [activeTab, setActiveTab] = useState('skills');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [savedProficiencies, setSavedProficiencies] = useState({});
  const [savedCategories, setSavedCategories] = useState({});
  const [savedDescriptions, setSavedDescriptions] = useState({});
  const [submissionMessage, setSubmissionMessage] = useState('');

  // Register tabs with PreviewContext for navigation
  useEffect(() => {
    if (registerSectionTabs) {
      registerSectionTabs(['skills', 'design'], activeTab, setActiveTab);
    }
    return () => {
      if (clearSectionTabs) clearSectionTabs();
    };
  }, [activeTab, registerSectionTabs, clearSectionTabs]);

  // Fetch existing skills on mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/career/skill/', {
          headers: { Authorization: `Token ${token}` },
        });
        const skillsData = Array.isArray(response.data) ? response.data : response.data.results || [];
        if (skillsData.length > 0) {
          setSelectedSkills(skillsData.map(s => s.name));
          const profs = {};
          const cats = {};
          const descs = {};
          skillsData.forEach(s => {
            profs[s.name] = s.proficiency || 50;
            cats[s.name] = s.category || 'other';
            descs[s.name] = s.description || '';
          });
          setSavedProficiencies(profs);
          setSavedCategories(cats);
          setSavedDescriptions(descs);
        }
      } catch (error) {
        console.log('No saved skills');
      }
    };
    fetchSkills();
  }, []);

  // Move from skills selection to design tab
  const handleSkillsSelected = (skills) => {
    setSelectedSkills(skills);
    setActiveTab('design');
  };

  // Handler for final form submission
  const handleSubmit = async (data) => {
    // Convert skill data to an array of skill objects with category
    const skillsPayload = Object.entries(data).map(([name, skillInfo]) => ({
      name,
      proficiency: skillInfo.proficiency,
      category: skillInfo.category || 'other',
    }));

    try {
      const token = localStorage.getItem('token');
      await api.post(
        'career/skill/bulk_create/',
        { skills: skillsPayload },
        {
          headers: { Authorization: `Token ${token}` },
          withCredentials: true,
        }
      );
      // Navigate to awards after successful save
      navigate('/dashboard/awards');
    } catch (error) {
      console.error('Error submitting skills:', error);
      setSubmissionMessage('There was an error saving your skills.');
    }
  };

  return (
    <div className="relative bg-gray-100 p-4 pb-24">
      {/* Proficiency Toggle */}
      <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg shadow-sm border border-gray-200">
        <div>
          <p className="text-sm font-semibold text-gray-800">Show Proficiency Percentage</p>
          <p className="text-xs text-gray-500">Display skill level (%) on your public profile</p>
        </div>
        <button
          onClick={handleToggleProficiency}
          className={`relative w-14 h-7 rounded-full transition-colors duration-200 ${
            showProficiency ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${
              showProficiency ? 'translate-x-7' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Tab Navigation - Only show when proficiency is enabled */}
      {showProficiency && (
        <div className="flex gap-1 mb-5 p-1 rounded-md" style={{ backgroundColor: '#0f172a' }}>
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-all duration-150"
                style={{
                  backgroundColor: isActive ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                  color: isActive ? '#6366f1' : '#64748b',
                }}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
                {tab.id === 'skills' && selectedSkills.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: isActive ? 'rgba(99, 102, 241, 0.3)' : 'rgba(100, 116, 139, 0.2)' }}>
                    {selectedSkills.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      {(activeTab === 'skills' || !showProficiency) && (
        <SkillsSelection
          onNext={handleSkillsSelected}
          updateLiveSkills={updateLiveSkills}
          savedProficiencies={savedProficiencies}
          setSavedProficiencies={setSavedProficiencies}
          savedCategories={savedCategories}
          setSavedCategories={setSavedCategories}
          savedDescriptions={savedDescriptions}
          setSavedDescriptions={setSavedDescriptions}
          parentSelectedSkills={selectedSkills}
          setParentSelectedSkills={setSelectedSkills}
          showProficiency={showProficiency}
        />
      )}

      {activeTab === 'design' && showProficiency && (
        <>
          {/* Proficiency Form */}
          {selectedSkills.length > 0 ? (
            <ProficiencyForm
              selectedSkills={selectedSkills}
              onSubmit={handleSubmit}
              savedProficiencies={savedProficiencies}
              savedCategories={savedCategories}
              savedDescriptions={savedDescriptions}
              updateLiveSkills={updateLiveSkills}
              showProficiency={showProficiency}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Skills Selected</h3>
              <p className="text-gray-500 mb-4">Please select your skills first before setting proficiency levels.</p>
              <button
                onClick={() => setActiveTab('skills')}
                className="px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition"
              >
                Go to Select Skills
              </button>
            </div>
          )}
        </>
      )}

      {submissionMessage && (
        <div className="max-w-lg mx-auto mt-6 p-6 bg-white shadow-md rounded-xl">
          <h3 className="text-lg font-semibold mb-2">Submission Status</h3>
          <p className="text-red-600">{submissionMessage}</p>
        </div>
      )}
    </div>
  );
};

export default SkillForm;
